import * as _ from 'lodash'
import {BehaviorSubject} from "rxjs";
import {IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Tag_Rule,
    esWB_Tag_Rule_Param
} from "../PA.Core/PAdbSchema";
import {
    WB_TagRuleRow,
    WB_TagRuleParamRow,
} from "../PA.Core/PAdbSchemaRows";
import {IwbTagRule} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";

export class TagRuleBO implements IBusinessObject
{
    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_TagRule: WB_TagRuleRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Tag Rule'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_TagRule = null;

        this.IHaveTagRuleParam_init();
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">

    get WB_TagRule(): WB_TagRuleRow {
        return this.drWB_TagRule;
    }

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Tag Rule");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for new Tag Rule.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            this.dbContext.createRecordWithKey(esWB_Tag_Rule)
                .then((newTagRuleRow: WB_TagRuleRow) => {
                    newTagRuleRow.setDefaults();

                    self.drWB_TagRule = newTagRuleRow;
                    self.dbContext.add(esWB_Tag_Rule, newTagRuleRow);
                    self.Identity.setAll(
                        self.WB_TagRule.cif.WB_TAG_RULE_ID,
                        self.WB_TagRule.cif.TAG_RULE_ID,
                        self.WB_TagRule.cif.TAG_RULE_NAME
                    );
                    self.LoadProgressProvider.setName(
                        'Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')'
                    );
                    self.Identity.listenToRecordUpdates(self.WB_TagRule, 'WB_TAG_RULE_ID', 'TAG_RULE_ID', 'TAG_RULE_NAME');

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            if (self.bIsLoaded) {
                const sError = 'Tag Rule already loaded.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for Tag Rule.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = TagRuleBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = TagRuleBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(() => {
                    const aTagRuleRow: any = recordResolver.get(esWB_Tag_Rule, anId);

                    if (_.isEmpty(aTagRuleRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception('Id not found.');
                        resolve(self);
                        return;
                    }

                    self.drWB_TagRule = aTagRuleRow;
                    self.Identity.setAll(
                        self.WB_TagRule.cif.WB_TAG_RULE_ID,
                        self.WB_TagRule.cif.TAG_RULE_ID,
                        self.WB_TagRule.cif.TAG_RULE_NAME
                    );
                    self.LoadProgressProvider.setName(
                        'Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')'
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_TagRule,
                        'WB_TAG_RULE_ID',
                        'TAG_RULE_ID',
                        'TAG_RULE_NAME'
                    );
                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                })
        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_TagRule.cif.TAG_RULE_NAME + '(' + self.WB_TagRule.cif.TAG_RULE_ID + ')'
        );
        return new Promise<IBusinessObject>((resolve, reject) => {
            self.ValidateOption = options || {};

            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Tag Rule.';
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            self._bIsValidated = true;
            self.ValidateProgressProvider.finish();
            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbTagRule = <IwbTagRule>self.drWB_TagRule;
        this.SaveProgressProvider.start('Saving ' + wbTagRule.TAG_RULE_NAME + '(' + wbTagRule.TAG_RULE_ID + ')');

        return new Promise<IBusinessObject>((resolve, reject) => {
            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Tag Rule';
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch)
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            else
                saveFunction = self.dbContext.Save.bind(self.dbContext);

            let tasks: Promise<any>[] = [];
            saveFunction(esWB_Tag_Rule, self.drWB_TagRule, {})
                .then(() => {
                    tasks.push(saveFunction(esWB_Tag_Rule_Param, self.drWB_TagRule.WB_TagRuleParamRows));
                    return Promise.all(tasks);
                })
                .then(() => {
                    if (bBatch)
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        });
                    else
                        self._bIsSaved = true;

                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self);
                })
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbTagRule = <IwbTagRule>self.drWB_TagRule;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start('Deleting ' + wbTagRule.TAG_RULE_NAME + '(' + wbTagRule.TAG_RULE_ID + ')');
        return new Promise<IBusinessObject>((resolve) => {
            // 1. Validate if we can delete the Customer
            // 2. If valid, mark all the records for deletion
            self.drWB_TagRule.markDeleted();

            if (bSaveNow) {
                this.SaveUsing(false)
                    .then(() => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const tag_rule_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        return new ESRecordResolver(dbContext, tag_rule_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Tag_Rule, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                })
        });
    }

    //</editor-fold>

    //<editor-fold desc="IHaveTagRuleParam" operations>
    private _OnTagRuleParamCollectionChanged: BehaviorSubject<TagRuleBO> = undefined;

    IHaveTagRuleParam_init() {
        this._OnTagRuleParamCollectionChanged = new BehaviorSubject<TagRuleBO>(this);
    }

    get OnTagRuleParamCollectionChanged(): BehaviorSubject<TagRuleBO> {
        return this._OnTagRuleParamCollectionChanged;
    }

    addTagRuleParam(options?: any): Promise<TagRuleBO> {
        const self = this;
        return new Promise<TagRuleBO>((resolve, reject) => {
           return self.dbContext.createRecordWithKey(esWB_Tag_Rule_Param)
               .then((aNewRow: WB_TagRuleParamRow) => {
                   aNewRow.setDefaults();
                   aNewRow.cif.WB_TAG_RULE_ID = self.WB_TagRule.cif.WB_TAG_RULE_ID;
                   self.dbContext.add(esWB_Tag_Rule_Param, aNewRow);
                   resolve(self);
               })
               .catch((err) => {
                   reject(err);
               });
        });
    }

    removeTagRuleParam(aTagRuleParamId: number, options?: any): Promise<TagRuleBO> {
        const self = this;
        return new Promise<TagRuleBO>((resolve, reject) => {
            _.forEach(self.WB_TagRule.WB_TagRuleParamRows, (aTagRuleParamRow: WB_TagRuleParamRow) => {
                if (aTagRuleParamRow.isActive && aTagRuleParamRow.cif.WB_TAG_RULE_PARAM_ID === aTagRuleParamId) {
                    aTagRuleParamRow.markDeleted();
                }
            });
            resolve(self);
        });
    }
    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( TagRuleBO, "TagRuleBO" ,"Tag Rule")

